import { useCallback, useEffect, useRef } from 'react';
import { ReactNode } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { flushSync } from 'react-dom';

export function useChartTooltip() {
  const rootRef = useRef<Root | null>(null);
  const tooltipElRef = useRef<HTMLDivElement | null>(null);

  const ensureTooltipEl = useCallback(() => {
    if (!tooltipElRef.current) {
      const el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.pointerEvents = 'none';
      el.style.zIndex = '2000';
      el.style.opacity = '0';
      document.body.appendChild(el);
      tooltipElRef.current = el;
      rootRef.current = createRoot(el);
    }
    return { el: tooltipElRef.current, root: rootRef.current! };
  }, []);

  // Hide tooltip immediately on any scroll (capture phase catches all sources)
  useEffect(() => {
    const hideOnScroll = () => {
      if (tooltipElRef.current) {
        tooltipElRef.current.style.opacity = '0';
      }
    };

    window.addEventListener('scroll', hideOnScroll, true);
    return () => {
      window.removeEventListener('scroll', hideOnScroll, true);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear content without synchronous unmount — avoids race condition with flushSync renders
      rootRef.current?.render(null);
      tooltipElRef.current?.remove();
      rootRef.current = null;
      tooltipElRef.current = null;
    };
  }, []);

  /**
   * Render tooltip content and position it relative to the hovered chart point.
   * Pass `null` as content to hide the tooltip.
   */
  const showTooltip = useCallback(
    (context: any, content: ReactNode | null) => {
      const { el, root } = ensureTooltipEl();
      const tooltip = context.tooltip;

      // Hide tooltip when Chart.js says opacity is 0 or no content
      if (!tooltip || tooltip.opacity === 0 || content === null) {
        el.style.opacity = '0';
        return;
      }

      // Render content synchronously so we can measure dimensions for positioning
      flushSync(() => {
        root.render(content);
      });

      // Position with boundary detection
      const canvas = context.chart.canvas;
      const canvasRect = canvas.getBoundingClientRect();
      const tooltipWidth = el.offsetWidth;
      const tooltipHeight = el.offsetHeight;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      const OFFSET_X = 50;
      let x = canvasRect.left + window.pageXOffset + tooltip.caretX + OFFSET_X;
      let y = canvasRect.top + window.pageYOffset + tooltip.caretY;

      if (x + tooltipWidth > screenWidth) x = x - tooltipWidth - OFFSET_X * 2;
      if (x < 0) x = 10;
      if (y + tooltipHeight > screenHeight) y -= tooltipHeight + 20;
      if (y < 0) y = 10;

      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.opacity = '1';
    },
    [ensureTooltipEl],
  );

  return { showTooltip };
}
